import { useEffect, useMemo, useState, useRef } from "react";
import api, { formatErr, ROLE_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/SearchableSelect";
import { Plus, Pencil, ShieldCheck, Upload, UserCheck, UserX, Crown } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "hotel_gm", hotel_id: null, region: "", department: "" };

export default function Users() {
  const { user } = useAuth();
  const isSuper = !!user.is_super;
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [meta, setMeta] = useState({ departments: [], clusters: [] });
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [hotelFilter, setHotelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [importOpen, setImportOpen] = useState(false);
  const [importHotel, setImportHotel] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/users?include_inactive=true").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/hotels").then((r) => setHotels(r.data));
    api.get("/meta").then((r) => setMeta(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load is stable; run once on mount
  }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/users/${editing}`, form);
      else await api.post("/users", form);
      toast.success(editing ? "User updated" : "User created");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const setActive = async (u, active) => {
    try {
      await api.put(`/users/${u.id}/active`, { active });
      toast.success(active ? `${u.name} activated` : `${u.name} deactivated`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("hotel_id", importHotel || "");
      const { data } = await api.post("/users/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (data.created > 0) load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setImporting(false);
    }
  };

  const hotelOptions = useMemo(() => hotels.map((h) => ({ value: h.id, label: h.name, sub: [h.city, h.region].filter(Boolean).join(" · ") })), [hotels]);
  const needsHotel = ["hotel_gm", "dept_head"].includes(form.role);
  const needsCluster = form.role === "regional_head";
  const needsDept = form.role === "dept_head";
  const canManage = (u) => (u.role === "corporate_admin" || u.is_super ? isSuper : true);

  const filtered = users.filter((u) =>
    (hotelFilter === "all" || u.hotel_id === hotelFilter) &&
    (statusFilter === "all" || (statusFilter === "active" ? !u.archived : !!u.archived)));

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Users</h1>
          <p className="text-stone-500 mt-1 text-sm">Company emails only (fernhotels.com / zinchotels.com / beaconhotels.com) — external auditors excepted.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="import-users-button" variant="outline" onClick={() => { setImportResult(null); setImportHotel(""); setImportOpen(true); }}>
            <Upload className="w-4 h-4 mr-2" />Import Users
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button" className="bg-[#14532D] hover:bg-[#166534]"><Plus className="w-4 h-4 mr-2" />Add User</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Name</Label>
                    <Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Password {editing && <span className="text-stone-400">(leave blank to keep)</span>}</Label>
                    <Input data-testid="user-form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROLE_LABELS).filter(([v]) => v !== "corporate_admin" || isSuper).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {needsHotel && (
                  <div className="space-y-1.5">
                    <Label>Hotel</Label>
                    <SearchableSelect testId="user-form-hotel" options={hotelOptions} value={form.hotel_id || ""} onChange={(v) => setForm({ ...form, hotel_id: v })} placeholder="Search hotel..." />
                  </div>
                )}
                {needsCluster && (
                  <div className="space-y-1.5">
                    <Label>Cluster</Label>
                    <Input data-testid="user-form-region" list="user-cluster-options" value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. West" />
                    <datalist id="user-cluster-options">{(meta.clusters || []).map((c) => <option key={c} value={c} />)}</datalist>
                  </div>
                )}
                {needsDept && (
                  <div className="space-y-1.5">
                    <Label>Department</Label>
                    <Select value={form.department || ""} onValueChange={(v) => setForm({ ...form, department: v })}>
                      <SelectTrigger data-testid="user-form-department"><SelectValue placeholder="Choose department" /></SelectTrigger>
                      <SelectContent>{meta.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <Button data-testid="user-form-save" onClick={save} disabled={!form.name || !form.email || (!editing && !form.password)} className="w-full bg-[#14532D] hover:bg-[#166534]">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-72">
          <SearchableSelect testId="users-hotel-filter" options={[{ value: "all", label: "All hotels" }, ...hotelOptions]} value={hotelFilter} onChange={setHotelFilter} placeholder="Filter by hotel" className="bg-white h-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-9 text-sm bg-white" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Deactivated</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="bg-stone-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Email</th>
              <th className="py-3 px-4">Role</th>
              <th className="py-3 px-4">Scope</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {filtered.map((u) => {
              const hotel = hotels.find((h) => h.id === u.hotel_id);
              return (
                <tr key={u.id} className={`border-b border-stone-100 last:border-0 hover:bg-stone-50/60 ${u.archived ? "opacity-50" : ""}`}>
                  <td className="py-3.5 px-4 text-sm font-medium text-stone-800">
                    <span className="flex items-center gap-1.5">{u.name}{u.is_super && <Crown className="w-3.5 h-3.5 text-amber-500" title="Super admin" />}</span>
                  </td>
                  <td className="py-3.5 px-4 text-sm text-stone-600">{u.email}</td>
                  <td className="py-3.5 px-4"><span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#D1FAE5] text-[#14532D]">{u.is_super ? "Super Admin" : ROLE_LABELS[u.role]}</span></td>
                  <td className="py-3.5 px-4 text-sm text-stone-600 max-w-[200px] truncate">{hotel?.name || u.region || u.department || "All"}</td>
                  <td className="py-3.5 px-4">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${u.archived ? "bg-stone-100 text-stone-500 border-stone-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={`user-status-${u.id}`}>
                      {u.archived ? "Deactivated" : "Active"}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-right whitespace-nowrap">
                    {user.role === "corporate_admin" && canManage(u) && (
                      <>
                        <button data-testid={`user-edit-${u.id}`} className="p-1.5 rounded hover:bg-stone-100" onClick={() => { setEditing(u.id); setForm({ name: u.name, email: u.email, password: "", role: u.role, hotel_id: u.hotel_id, region: u.region || "", department: u.department || "" }); setOpen(true); }}>
                          <Pencil className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                        </button>
                        {u.id !== user.id && !u.is_super && (
                          u.archived ? (
                            <button data-testid={`user-activate-${u.id}`} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title="Activate" onClick={() => setActive(u, true)}>
                              <UserCheck className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          ) : (
                            <button data-testid={`user-deactivate-${u.id}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Deactivate" onClick={() => setActive(u, false)}>
                              <UserX className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-sm text-stone-400">No users match.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="import-users-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-[#14532D]" />Import Users</DialogTitle>
            <DialogDescription>
              Upload a .csv or .xlsx with columns: <b>Name, Email, Role, Department</b>. Emails must be company domains (external auditors excepted). Bad rows are skipped and reported. Imported users set their password via "Forgot password".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Hotel (applied to GM / Department Head rows)</Label>
              <SearchableSelect testId="import-hotel-select" options={hotelOptions} value={importHotel} onChange={setImportHotel} placeholder="Search hotel..." />
            </div>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => { doImport(e.target.files[0]); e.target.value = ""; }} />
            <Button data-testid="import-file-button" className="w-full bg-[#14532D] hover:bg-[#166534]" disabled={importing} onClick={() => fileRef.current?.click()}>
              {importing ? "Importing..." : "Choose file & import"}
            </Button>
            {importResult && (
              <div className="space-y-2" data-testid="import-result">
                <p className="text-sm font-semibold text-emerald-700">{importResult.created} user{importResult.created === 1 ? "" : "s"} created.</p>
                {importResult.errors.length > 0 && (
                  <div className="border border-rose-200 bg-rose-50 rounded-lg p-3 space-y-1 max-h-40 overflow-y-auto">
                    <p className="text-xs font-bold text-rose-700 uppercase">{importResult.errors.length} row{importResult.errors.length === 1 ? "" : "s"} skipped</p>
                    {importResult.errors.map((er, i) => <p key={`${er.row}-${er.email || i}`} className="text-xs text-rose-700">Row {er.row} ({er.email || "no email"}): {er.reason}</p>)}
                  </div>
                )}
                <p className="text-xs text-stone-500">{importResult.note}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
